/**
 * Bärli. (06.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin: *„Ich möchte ein kleines verstecktes Bärli da aufsetzen. Wenn ich
 * die Seite runterziehe … bei der Claude-App geht das auch, da ist der kleine
 * Claude-Roboter, der oben an der Kante herumläuft."*
 *
 * Und, mit einem Bild dazu: *„So soll der Bär aussehen!"* — ein frontaler
 * Teddy in Pixelgrafik, braun, mit hellerer Schnauze, Bauchfleck und
 * Innenohren. Deshalb ist er hier ein PIXELBILD und keine Strichzeichnung:
 * die erste Fassung war fein liniert in `currentColor`, das war nicht, was
 * er wollte.
 *
 * ## Wie er versteckt bleibt
 *
 * ⚠️ Die erste Fassung schob ihn mit `margin-top: -46px` nach oben. Das war
 *    falsch, und Martin hat es sofort gesehen: *„Der sollte nur sichtbar
 *    sein, wenn man runterzieht, sonst nicht. Da oben ist jetzt eine
 *    Strichliste."* Ein negativer Rand verschiebt die Schachtel nur — er
 *    verdeckt sie nicht. Der Bär stand mitten im Bild und drückte den
 *    Inhalt zusätzlich nach unten.
 *
 * Richtig ist: `position: absolute; top: -46px` im Blatt. Damit liegt er
 * ausserhalb des scrollbaren Bereichs von `#view-zuhause` (der nach oben
 * nicht scrollt) und wird vom Scroller weggeschnitten. Beim Überziehen —
 * dem Gummiband, das iOS und Safari über den Anfang hinaus zeigen —
 * verschiebt sich der Inhalt nach unten und gibt ihn frei.
 *
 * ⚠️ Wo die Plattform kein Gummiband hat (Chrome am Schreibtisch), bleibt er
 *    unsichtbar. Das ist kein Fehler, sondern die Bedingung dafür, dass er
 *    ohne eine Zeile Laufzeit-Code auskommt.
 */

/** Ein Pixel je Zeichen. Punkt heisst durchsichtig. */
const FARBEN: Readonly<Record<string, string>> = {
  D: "#5c3a24" /* Umriss */,
  M: "#85552f" /* Fell */,
  L: "#b5825a" /* Schnauze, Bauch, Innenohr, Pfoten */,
  K: "#17120e" /* Augen und Nase */,
};

/**
 * Der Bär, 16 x 20 Pixel.
 *
 * Die beiden letzten Zeilen sind die Füsse — sie werden getrennt gezeichnet,
 * damit sie sich beim Gehen abwechseln können.
 */
const KOERPER = [
  ".DDDD......DDDD.",
  ".DLLD......DLLD.",
  ".DLLDDDDDDDDLLD.",
  "..DDMMMMMMMMDD..",
  "..DMMMMMMMMMMD..",
  "..DMKMMMMMMKMD..",
  "..DMMMMMMMMMMD..",
  "..DMMLLLLLLMMD..",
  "..DMMLKKKKLMMD..",
  "...DMLLKKLLMD...",
  "...DMMLLLLMMD...",
  "....DDMMMMDD....",
  "....DDDDDDDD....",
  ".DDDMMLLLLMM....",
  "DLLDMLLLLLLM....",
  "DLLDMLLLLLLM....",
  ".DDDMMLLLLMM....",
  "....DMMMMMMD....",
] as const;

/**
 * Der rechte Arm, getrennt vom Körper (10.10.2026): unten hängt er am Bauch, zum Winken wandert er hoch. Zwei Fassungen
 * der Pfote oben (eine Zeile höher/tiefer) geben das Winken.
 */
const ARM_RECHTS = ["DDD.", "DLLD", "DLLD", "DDD."] as const;
const ARM_X = 12;
const ARM_UNTEN_Y = 13;
const ARM_OBEN_Y = 7;

/** Das Lid: deckt die beiden Augen mit Fell — ein Blinzeln. Augen bei x=4 und x=11, Zeile 5. */
const LID = ["....M......M...."] as const;
const LID_ZEILE = 5;

/** Nur die Füsse — links und rechts getrennt, für den Gang. */
const FUSS_LINKS = ["...DLLD.........", "...DDDD........."] as const;
const FUSS_RECHTS = [".........DLLD...", ".........DDDD..."] as const;
/** Auf welcher Zeile die Füsse stehen. */
const FUSS_ZEILE = KOERPER.length;

/**
 * Zeilen in Rechtecke übersetzen.
 *
 * Gleichfarbige Pixel nebeneinander werden zu EINEM Rechteck zusammengefasst.
 * Das ist kein Geiz, sondern der Unterschied zwischen rund 200 Knoten und
 * rund 60 — der Streifen liegt in jedem Aufbau des Zuhause-Blatts.
 */
function pixelZuRechtecken(zeilen: readonly string[], versatzY: number, versatzX = 0): string {
  const stuecke: string[] = [];
  zeilen.forEach((zeile, y) => {
    let x = 0;
    while (x < zeile.length) {
      const z = zeile[x]!;
      if (!(z in FARBEN)) {
        x++;
        continue;
      }
      let breite = 1;
      while (zeile[x + breite] === z) breite++;
      stuecke.push(
        `<rect x="${x + versatzX}" y="${y + versatzY}" width="${breite}" height="1" fill="${FARBEN[z]}"/>`,
      );
      x += breite;
    }
  });
  return stuecke.join("");
}

/**
 * Der Streifen mit dem Bärli.
 *
 * Seit 10.10.2026 mehr als Hin- und Herlaufen (Martin: „kann er auch noch mehr machen als hin und her laufen?“): ein
 * Zyklus von 40 Sekunden — geht nach rechts, bleibt stehen und winkt, hüpft zweimal, geht zurück, setzt sich und döst.
 * Alles in CSS (`style.css`, „Bärli“), ohne Laufzeit-Code; die Schichten hier sind die Stellungen:
 *   Füße im Gang (a, b)  nur beim Gehen — sonst stehen sie still
 *   rechter Arm          unten, oder zum Winken oben (zwei Fassungen)
 *   Lid                  das Blinzeln
 * Die Füsse liegen in zwei Gruppen, die sich im Blatt abwechseln — so entsteht der Gang, ohne dass irgendetwas gerechnet wird.
 */
export function baerliStreifen(): string {
  return `<div class="zuhause-baerli" aria-hidden="true">
    <div class="zuhause-baerli-lauf">
      <div class="zuhause-baerli-hupf">
        <svg class="zuhause-baerli-tier" viewBox="0 0 16 20" width="32" height="40"
          shape-rendering="crispEdges" focusable="false">
          ${pixelZuRechtecken(KOERPER, 0)}
          <g class="zuhause-baerli-fuss-still">${pixelZuRechtecken(FUSS_LINKS, FUSS_ZEILE)}${pixelZuRechtecken(FUSS_RECHTS, FUSS_ZEILE)}</g>
          <g class="zuhause-baerli-fuss zuhause-baerli-fuss--a">${pixelZuRechtecken(FUSS_LINKS, FUSS_ZEILE)}</g>
          <g class="zuhause-baerli-fuss zuhause-baerli-fuss--b">${pixelZuRechtecken(FUSS_RECHTS, FUSS_ZEILE)}</g>
          <g class="zuhause-baerli-arm-unten">${pixelZuRechtecken(ARM_RECHTS, ARM_UNTEN_Y, ARM_X)}</g>
          <g class="zuhause-baerli-winke">
            <g class="zuhause-baerli-winke--a">${pixelZuRechtecken(ARM_RECHTS, ARM_OBEN_Y, ARM_X)}</g>
            <g class="zuhause-baerli-winke--b">${pixelZuRechtecken(ARM_RECHTS, ARM_OBEN_Y + 1, ARM_X)}</g>
          </g>
          <g class="zuhause-baerli-lid">${pixelZuRechtecken(LID, LID_ZEILE)}</g>
        </svg>
      </div>
    </div>
  </div>`;
}
